/* cspell:disable */
import React, { ComponentProps, useState } from 'react';
import rehypeRaw from 'rehype-raw';
import {
  ChatMarkdown,
  CUSTOM_COMPONENTS,
} from '@plerionhq/design-system/unstable';
import rehypeSanitize from 'rehype-sanitize';
import { defaultSchema } from 'hast-util-sanitize';
import {
  Mermaid,
  MarkdownWidget,
  MarkdownDashboard,
  MarkdownAssets,
  MarkdownFindings,
  MarkdownRisks,
  MarkdownVulnerabilities,
  MarkdownAttackPath,
  MarkdownFactCheck,
} from './markdown';
import { MarkdownVulnerabilityInvestigation } from './markdown/VulnerabilityInvestigation';
import { visit } from 'unist-util-visit';
import type { Node } from 'unist';
import type { Element, Text as HastText } from 'hast';
import './ExtendedMarkdown.css';
import { useTranslation } from 'react-i18next';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CodeComponent = CUSTOM_COMPONENTS.code as React.FC<any>;

const customSchema = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames || []),
    'context',
    'usermessage',
    'contextinstruction',
    'pagecontent',
    'pagetitle',
    'pageurl',
    'thinking',
    'followup',
    'user-memory',
    'pleri-followup',
    'file-content',
    'instructions',
  ],
  attributes: {
    ...defaultSchema.attributes,
    context: [], // Allow <context> with no attributes
    usermessage: [], // Allow <usermessage> with no attributes
    contextinstruction: [], // Allow <contextinstruction> with no attributes
    pagecontent: [], // Allow <pagecontent> with no attributes
    pagetitle: [], // Allow <pagetitle> with no attributes
    pageurl: [], // Allow <pageurl> with no attributes
    thinking: [], // Allow <thinking> with no attributes
    'pleri-followup': [], // Allow <pleri-followup> with no attributes
    'file-content': ['filename'], // Allow <file-content> with no attributes
  },
};

// Accordion component for context
const ContextAccordion: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="my-1 overflow-hidden rounded border border-gray-100 text-xs dark:border-gray-800">
      <button
        className="flex w-full items-center justify-between bg-gray-50/50 p-1.5 text-left text-gray-500 hover:bg-gray-50 dark:bg-gray-800/50 dark:text-gray-400 dark:hover:bg-gray-800"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="font-normal text-gray-500 dark:text-gray-400">
          {title}
        </span>
        <svg
          className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {isOpen && (
        <div className="bg-white p-1.5 dark:bg-gray-900">
          <div className="prose prose-xs max-w-none">{children}</div>
        </div>
      )}
    </div>
  );
};

// Code block component for context content
const ContextCodeBlock: React.FC<{
  children: React.ReactNode;
  language?: string;
}> = ({ children, language = 'markdown' }) => {
  return (
    <div className="overflow-x-auto rounded bg-gray-50/50 p-2 dark:bg-gray-800/50">
      <pre className="text-xs">
        <code className={`language-${language}`}>{children}</code>
      </pre>
    </div>
  );
};

// 1) Plugin: convert usermessage:XYZ text → bold text "XYZ:"
const rehypeStripUserMessage = () => {
  return (tree: Node) => {
    visit(tree, 'text', (node: HastText, index: number, parent: Element) => {
      if (
        node.value &&
        (node.value.startsWith('usermessage:') ||
          node.value.startsWith('UserMessage:'))
      ) {
        const [, content = ''] = node.value.split(':');
        if (content.trim()) {
          const strongElement: Element = {
            type: 'element',
            tagName: 'strong',
            properties: {},
            children: [{ type: 'text', value: `${content}: ` }],
          };
          parent.children.splice(index, 1, strongElement);
        }
      }
    });
  };
};

const AttachedContext = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation('pleri/chat');
  return (
    <ContextAccordion title={t('attachedContextHeading')}>
      {children}
    </ContextAccordion>
  );
};

const PageUrl = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation('pleri/chat');
  return (
    <ContextAccordion title={t('pageUrl')}>
      <ContextCodeBlock>{children}</ContextCodeBlock>
    </ContextAccordion>
  );
};

const PageTitle = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation('pleri/chat');
  return (
    <ContextAccordion title={t('pageTitle')}>
      <ContextCodeBlock>{children}</ContextCodeBlock>
    </ContextAccordion>
  );
};

const PageContent = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation('pleri/chat');
  const content = children?.toString() || '';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderItem = (item: any) => {
    if (typeof item === 'string') {
      return <ExtendedMarkdown content={item} />;
    }
    return item;
  };

  return (
    <ContextAccordion title={t('pageContent')}>
      {Array.isArray(content) ? (
        content.map((item, index) => <div key={index}>{renderItem(item)}</div>)
      ) : (
        <ExtendedMarkdown content={content} />
      )}
    </ContextAccordion>
  );
};

const components = {
  pre: ({ children, ...props }: { children: React.ReactNode }) => {
    const childArray = React.Children.toArray(children);
    // Custom code blocks are rendered by our code component;
    // pre receives the code element, not the custom component output, so detect via className.
    const hasCustomCodeBlock = childArray.some((child) => {
      if (
        !React.isValidElement<{ className?: string }>(child) ||
        !child.props.className
      ) {
        return false;
      }
      const className = String(child.props.className);
      return (
        /language-assets\b/.test(className) ||
        /language-findings\b/.test(className) ||
        /language-risks\b/.test(className) ||
        /language-vulnerabilities\b/.test(className) ||
        /language-attackPath\b/.test(className) ||
        /language-vulnerabilityInvestigation\b/.test(className) ||
        /language-factCheck\b/.test(className)
      );
    });

    if (hasCustomCodeBlock) {
      return <div className="not-prose">{children}</div>;
    }

    return <pre {...props}>{children}</pre>;
  },
  context: AttachedContext,
  contextinstruction: () => {
    return <></>;
  },
  'pleri-followup': () => {
    return <></>;
  },
  pagecontent: PageContent,
  followup: ({ children }: { children: React.ReactNode }) => {
    return <ContextCodeBlock>{children}</ContextCodeBlock>;
  },
  pageurl: PageUrl,
  pagetitle: PageTitle,
  usermessage: ({ children }: { children: React.ReactNode }) => {
    return <>{children}</>;
  },
  thinking: ({ children }: { children: React.ReactNode }) => {
    // show quotes markdown like style
    return (
      <div className="my-2 italic">
        <ChatMarkdown
          content={`> ${children?.toString().replaceAll('\n', '\n> ')}`}
        />
      </div>
    );
  },
  code: ({
    children,
    className,
    ...otherProps
  }: {
    children: React.ReactNode;
    className: string;
  }) => {
    const language =
      /lang-(\w+)/.exec(className || '')?.[1] ||
      /language-(\w+)/.exec(className || '')?.[1] ||
      '';

    const code =
      typeof children === 'string'
        ? children
        : Array.isArray(children)
          ? children.join('')
          : '';

    if (language === 'mermaid') {
      return <Mermaid code={code} className="my-2" />;
    }

    if (language === 'widget') {
      return <MarkdownWidget code={code} />;
    }

    if (language === 'dashboard') {
      return <MarkdownDashboard code={code} />;
    }

    if (language === 'assets') {
      return <MarkdownAssets code={code} />;
    }

    if (language === 'findings') {
      return <MarkdownFindings code={code} />;
    }

    if (language === 'risks') {
      return <MarkdownRisks code={code} />;
    }

    if (language === 'vulnerabilities') {
      return <MarkdownVulnerabilities code={code} />;
    }

    if (language === 'attackPath') {
      return <MarkdownAttackPath code={code} />;
    }

    if (language === 'factCheck') {
      return <MarkdownFactCheck code={code} />;
    }

    if (language === 'vulnerabilityInvestigation') {
      return <MarkdownVulnerabilityInvestigation code={code} />;
    }

    // fallback to default code block
    return (
      <CodeComponent className={className} {...otherProps}>
        {children}
      </CodeComponent>
    );
  },
  'user-memory': () => <></>,
  instructions: () => <></>,
  'file-content': ({
    filename,
    children,
  }: {
    filename: string;
    children: React.ReactNode;
  }) => {
    return (
      <ContextAccordion title={filename}>
        <ExtendedMarkdown
          content={`
~~~
${children}
~~~
`}
        ></ExtendedMarkdown>
      </ContextAccordion>
    );
  },
} as ComponentProps<typeof ChatMarkdown>['customComponents'];

export const ExtendedMarkdown: React.FC<
  ComponentProps<typeof ChatMarkdown> & { content: string; isLoading?: boolean }
> = ({ ...props }) => {
  return (
    <div className="markdown-content w-full max-w-full">
      <ChatMarkdown
        rehypePlugins={[
          rehypeStripUserMessage,
          rehypeRaw,
          [rehypeSanitize, customSchema], // apply custom sanitation
        ]}
        textAnimation={{
          animate: false,
          letterPerDuration: 100,
        }}
        customComponents={components}
        {...props}
        content={`${props.content}`}
      />
    </div>
  );
};
